/* Chấm công, tính lương theo "Bảng lương FAS", bảo hiểm, thuế TNCN, thang bảng lương, thông số theo thời gian, chính sách KPI.
   Port nguyên văn từ app cũ (bản v2). Các hàm cần dữ liệu chung nhận một PayEnv thay cho biến toàn cục của app cũ. */
import { CK_KINDS, type ContractKind } from './hr';
import { fmt } from './money';

/* ---------- mã chấm công ---------- */
export type TsCol = 'AN' | 'AO' | 'AP' | 'AQ' | 'AR' | 'AS' | 'AT' | 'AU' | 'AV' | 'AW' | 'AX';

/**
 * Mã chấm công theo mẫu "Bảng chấm công" FAS: [mã, tên, nhóm cột → giá trị]
 * (AN làm việc · AO phép · AP lễ · AQ không lương · AR chế độ hưởng lương · AS chế độ BHXH · AT thai sản · AU tăng ca cuối tuần · AV tăng ca lễ · AW giảm giờ làm · AX nghỉ cuối tuần)
 */
export const TS_CODES: [string, string, Partial<Record<TsCol, number>>][] = [
  ['1', 'Đi làm', { AN: 1 }], ['0.5/P', 'Nửa ngày làm + nửa ngày phép', { AN: 0.5, AO: 0.5 }], ['P', 'Nghỉ phép năm', { AO: 1 }], ['0.5/KL', 'Nửa ngày làm + nửa không lương', { AN: 0.5, AQ: 0.5 }], ['KL', 'Nghỉ không lương', { AQ: 1 }],
  ['L', 'Đi làm ngày lễ (1 + tăng ca lễ)', { AP: 1, AV: 1 }], ['NL', 'Nghỉ lễ hưởng lương', { AP: 1 }], ['CD', 'Nghỉ chế độ hưởng nguyên lương', { AR: 1 }], ['OD', 'Ốm đau (BHXH trả)', { AS: 1 }], ['DS', 'Dưỡng sức (BHXH trả)', { AS: 1 }], ['TS', 'Thai sản', { AT: 1 }],
  ['GGL', 'Giảm giờ làm (hưởng lương)', { AW: 1 }], ['0.5/GGL', 'Nửa ngày làm + nửa giảm giờ làm', { AN: 0.5, AW: 0.5 }], ['TB', 'Tính như ngày làm việc (TB)', { AN: 1 }], ['TC', 'Tăng ca cuối tuần', { AU: 1 }], ['OFF', 'Nghỉ cuối tuần', { AX: 1 }],
];
export const TSC: Record<string, [string, string, Partial<Record<TsCol, number>>]> = Object.fromEntries(TS_CODES.map(c => [c[0], c]));
export const TS_COLS: [TsCol, string][] = [['AN', 'Ngày công làm việc'], ['AO', 'Nghỉ phép năm'], ['AP', 'Nghỉ lễ'], ['AQ', 'Nghỉ không lương'], ['AR', 'Nghỉ chế độ (hưởng nguyên lương)'], ['AS', 'Nghỉ chế độ BHXH'], ['AT', 'Nghỉ thai sản'], ['AU', 'Tăng ca cuối tuần'], ['AV', 'Tăng ca ngày lễ'], ['AW', 'Giảm giờ làm'], ['AX', 'Nghỉ cuối tuần']];
/** Lớp màu của ô chấm công. */
export const tsCls = (c: string | null | undefined): string => c ? 'c-' + (({ '1': 'W', '0.5/P': 'HP', '0.5/KL': 'HKL', '0.5/GGL': 'HG' } as Record<string, string>)[c] || c) : '';

export const KPI_RANK: [string, string][] = [['A', 'Xuất sắc (91–100)'], ['B', 'Rất tốt (81–90)'], ['C', 'Đạt yêu cầu (66–80)'], ['D', 'Trung bình (51–65)'], ['F', 'Kém (<50)']];

/** Phụ cấp: [khóa, tên, mặc định chịu thuế]. */
export const ALLOW: [string, string, number][] = [
  ['lunch', 'HT ăn trưa', 0], ['fuel', 'HT xăng xe', 1], ['phone', 'HT điện thoại', 0], ['stationery', 'HT văn phòng phẩm', 0],
  ['travel', 'HT công tác phí', 0], ['perf', 'Lương thực hiện công việc (THCV)', 1], ['resp', 'Hỗ trợ trách nhiệm', 1],
];

/* ---------- thông số tính lương ---------- */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PayParams = { [k: string]: any; id?: string; from?: string; note?: string };

export const PAY0: Record<string, number> = {
  pers: 15500000, dep: 6200000, eSI: 8, eHI: 1.5, eUI: 1, cSI: 17.5, cHI: 3, cUI: 1, cTU: 2, cTNLD: 0.5, capSI: 50600000, capUI: 106200000, noInsDays: 14, lunchCap: 1200000, vppCap: 400000,
  flatTax: 10, flatMin: 5000000, holMul: 3, kpiA: 100, kpiB: 100, kpiC: 100, kpiD: 90, kpiF: 80,
};
/** Lương tối thiểu vùng (NĐ 293/2025/NĐ-CP) và lương cơ sở. */
export const PAR_DEF: Record<string, number> = { mw1: 5310000, mw2: 4730000, mw3: 4140000, mw4: 3700000, baseWage: 2340000 };
export const PAR_GROUPS: [string, [string, string][]][] = [
  ['Lương tối thiểu vùng & lương cơ sở', [['mw1', 'Vùng I'], ['mw2', 'Vùng II'], ['mw3', 'Vùng III'], ['mw4', 'Vùng IV'], ['baseWage', 'Lương cơ sở']]],
  ['Giảm trừ gia cảnh', [['pers', 'Giảm trừ bản thân / tháng'], ['dep', 'Giảm trừ mỗi người phụ thuộc / tháng']]],
  ['Trích bảo hiểm – người lao động (%)', [['eSI', 'BHXH'], ['eHI', 'BHYT'], ['eUI', 'BHTN']]],
  ['Trích bảo hiểm – công ty (%)', [['cSI', 'BHXH'], ['cHI', 'BHYT'], ['cUI', 'BHTN'], ['cTU', 'KPCĐ'], ['cTNLD', 'BH TNLĐ-BNN (đối tượng chỉ đóng TNLĐ)']]],
  ['Mức trần & điều kiện đóng bảo hiểm', [['capSI', 'Trần lương đóng BHXH, BHYT'], ['capUI', 'Trần lương đóng BHTN'], ['noInsDays', 'Không đóng BH khi nghỉ không lương từ (ngày)']]],
  ['Thuế TNCN & khác', [['lunchCap', 'Ăn trưa miễn thuế tối đa'], ['vppCap', 'VPP miễn thuế tối đa'], ['flatTax', 'Thuế khấu trừ TV/DV (%)'], ['flatMin', 'Ngưỡng khấu trừ thuế TV/DV'], ['holMul', 'Hệ số tăng ca ngày lễ (mã L)']]],
];
export const PAR_KEYS: string[] = PAR_GROUPS.flatMap(g => g[1].map(x => x[0]));
export const KPI_BASE: Record<string, string> = { perf: 'Lương THCV', perf_resp: 'Lương THCV + Hỗ trợ trách nhiệm' };

/** Biểu thuế TNCN lũy tiến 5 bậc (thu nhập tính thuế/tháng). */
export const PIT_BR: [number, number][] = [[10e6, 0.05], [30e6, 0.1], [60e6, 0.2], [100e6, 0.3], [Infinity, 0.35]];

export function pit(tni: number): number {
  let tax = 0, prev = 0;
  for (const [cap, r] of PIT_BR) {
    if (tni <= prev) break;
    tax += (Math.min(tni, cap) - prev) * r;
    prev = cap;
  }
  return Math.round(tax);
}

/* ---------- lịch tháng ---------- */
export const daysIn = (m: string): number => new Date(+m.slice(0, 4), +m.slice(5, 7), 0).getDate();
export const dowOf = (m: string, d: number): number => new Date(+m.slice(0, 4), +m.slice(5, 7) - 1, d).getDay();
export const DOW = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

/** Công chuẩn của tháng: bỏ Chủ nhật, và thứ 7 nếu không làm thứ 7. */
export function stdDays(m: string, sat: boolean): number {
  let n = 0;
  for (let d = 1; d <= daysIn(m); d++) { const w = dowOf(m, d); if (w !== 0 && (sat || w !== 6)) n++; }
  return n;
}

/* ---------- kiểu dữ liệu ---------- */
export interface EmployeeLite {
  id: string;
  code?: string;
  fullName?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  baseSalary?: number | string;
  insSalary?: number | string;
  dependents?: number | string;
  allowTax?: number | string;
  allowFree?: number | string;
  salSrc?: string;
  payType?: string;
  insType?: string;
  kpiScheme?: string;
  [k: string]: unknown;
}

export interface SalaryContract {
  id?: string;
  kind: ContractKind;
  empId?: string;
  no?: string;
  signDate?: string;
  fromDate?: string;
  toDate?: string;
  salary?: number | string;
  insSalary?: number | string;
  salGrade?: string;
  salStep?: number | string;
  [k: string]: unknown;
}

export interface Timesheet {
  month?: string;
  sat?: boolean;
  std?: number;
  cells?: Record<string, (string | null)[]>;
}

export interface PayInputs {
  days?: number | string | null; resp?: number | string | null; kpi?: string;
  kqkd?: number | string; lg?: number | string; peak?: number | string; biz?: number | string; adj?: number | string;
  taxDed?: number | string; taxRef?: number | string; adv?: number | string;
  [k: string]: unknown;
}

export interface KpiRank { r: string; l?: string; pct: number | string }
export interface KpiScheme { id: string; name: string; base: 'perf' | 'perf_resp' | string; ranks: KpiRank[] }
export interface SalScale { id: string; code: string; name: string; positions?: string; note?: string; pct?: number; steps: number[] }

export interface PaySettings {
  pay?: Record<string, number> | null;
  payVers?: PayParams[] | null;
  kpiSchemes?: KpiScheme[] | null;
  minWageRegion?: string | null;
  salScale?: SalScale[] | null;
}

export interface PayrollDoc {
  inputs?: Record<string, PayInputs>;
  locked?: boolean;
  snapshot?: (Record<string, unknown> & { id: string })[];
  cfg?: PayParams | null;
  [k: string]: unknown;
}

/** Dữ liệu chung mà các hàm lương đọc (tương ứng biến toàn cục S, employees, contracts, timesheets, payrolls của app cũ). */
export interface PayEnv {
  settings: PaySettings;
  employees: EmployeeLite[];
  contracts: SalaryContract[];
  timesheets: Record<string, Timesheet>;
  payrolls: Record<string, PayrollDoc>;
  today: string;
}

const ym = (env: PayEnv, d?: string): string => (d || env.today).slice(0, 7);

/** Nhân sự có mặt trong tháng m (đã vào làm và chưa nghỉ trước đầu tháng). */
export function empInMonth(e: Pick<EmployeeLite, 'startDate' | 'endDate'>, m: string): boolean {
  const s = m + '-01', en = m + '-' + String(daysIn(m)).padStart(2, '0');
  return (e.startDate || '0000') <= en && (!e.endDate || e.endDate >= s);
}

/* ---------- thông số theo thời gian, KPI ---------- */
export function parVers(env: PayEnv): PayParams[] {
  const S = env.settings;
  const l = Array.isArray(S.payVers) && S.payVers.length ? S.payVers
    : [{ id: 'v0', from: '2026-01', note: 'Thông số đang dùng', ...Object.fromEntries(PAR_KEYS.map(k => [k, ({ ...PAY0, ...PAR_DEF, ...(S.pay || {}) } as Record<string, number>)[k]])) }];
  return l.slice().sort((a, b) => (a.from || '').localeCompare(b.from || ''));
}

/** Bộ thông số áp dụng cho tháng m (mặc định tháng hiện tại): bộ có "từ tháng" gần nhất không vượt quá m. */
export function P(env: PayEnv, m?: string): PayParams {
  const vs = parVers(env), mm = m || ym(env);
  let v = vs[0];
  for (const x of vs) if ((x.from || '') <= mm) v = x;
  return { ...PAY0, ...PAR_DEF, ...(env.settings.pay || {}), ...v };
}

export const minW = (env: PayEnv, reg?: string | number, m?: string): number => +P(env, m)['mw' + (reg || env.settings.minWageRegion || '1')] || 0;

export function kpiSchemes(env: PayEnv): KpiScheme[] {
  const S = env.settings;
  if (Array.isArray(S.kpiSchemes) && S.kpiSchemes.length) return S.kpiSchemes;
  const p = { ...PAY0, ...(S.pay || {}) } as Record<string, number>;
  return [{ id: 'std', name: 'KPI chung', base: 'perf', ranks: KPI_RANK.map(([r, l]) => ({ r, l, pct: +p['kpi' + r] })) }];
}

/** Chính sách KPI của người lao động: 'none' = không áp KPI; trống/không còn = chính sách đầu tiên. */
export function empKpi(env: PayEnv, e: Pick<EmployeeLite, 'kpiScheme'>): KpiScheme | null {
  if (e.kpiScheme === 'none') return null;
  const l = kpiSchemes(env);
  return l.find(x => x.id === e.kpiScheme) || l[0] || null;
}

/* ---------- chấm công ---------- */
export interface TimesheetSum {
  n: Record<TsCol, number>;
  /** Tổng ngày công tính lương (AY). */
  AY: number;
  work: number;
  paid: number;
  unpaid: number;
}

/** Tổng ngày công tính lương (AY) = AN + AO + AP + AR + AU + AV × hệ số tăng ca lễ + AW (theo mẫu Bảng chấm công FAS). */
export function tsSum(env: PayEnv, codes: (string | null | undefined)[] | undefined): TimesheetSum {
  const n: Record<TsCol, number> = { AN: 0, AO: 0, AP: 0, AQ: 0, AR: 0, AS: 0, AT: 0, AU: 0, AV: 0, AW: 0, AX: 0 };
  for (const c of codes || []) {
    const d = c && TSC[c]?.[2];
    if (d) for (const k in d) n[k as TsCol] += d[k as TsCol] as number;
  }
  const mul = +P(env).holMul || 0, AY = n.AN + n.AO + n.AP + n.AR + n.AU + n.AV * mul + n.AW;
  return { n, AY: Math.round(AY * 100) / 100, work: n.AN, paid: Math.round(AY * 100) / 100, unpaid: n.AQ + n.AS + n.AT };
}

/* ---------- thang bảng lương, lương theo hợp đồng ---------- */
export const MINW: Record<number, number> = { 1: 5310000, 2: 4730000, 3: 4140000, 4: 3700000 };
export const scales = (env: PayEnv): SalScale[] => (env.settings.salScale || []).slice().sort((a, b) => (a.code || '').localeCompare(b.code || '', 'vi', { numeric: true }));
export const scaleById = (env: PayEnv, id: string | undefined | null): SalScale | undefined => (env.settings.salScale || []).find(x => x.id === id);
export const gradeAmt = (env: PayEnv, g: string | undefined | null, st: number | string | undefined | null): number => { const x = scaleById(env, g); return x && st ? +(x.steps?.[+st - 1] as number) || 0 : 0; };

/** Nhãn thang/bậc lương dạng chữ: "A2 · Nhóm 2 – bậc 3 (9.500.000)". */
export function gradeLabel(env: PayEnv, g: string | undefined | null, st?: number | string | null): string {
  const x = scaleById(env, g);
  if (!x) return '';
  const amt = gradeAmt(env, g, st);
  return `${x.code} · ${x.name}${st ? ` – bậc ${st}` : ''}${st && amt ? ` (${fmt(amt, 'VND')})` : ''}`;
}

type AllowVals = Record<string, number | string>;
/** Mức lương lấy từ một hợp đồng / phụ lục. */
export function conSal(c: SalaryContract): { baseSalary: number; insSalary: number | ''; [k: string]: number | string } {
  const v: { baseSalary: number; insSalary: number | ''; [k: string]: number | string } = { baseSalary: +(c.salary as number) || 0, insSalary: +(c.insSalary as number) || '' };
  if (ALLOW.some(([k]) => c['a_' + k] !== undefined && c['a_' + k] !== '')) ALLOW.forEach(([k]) => { v['a_' + k] = +(c['a_' + k] as number) || 0; });
  return v;
}

/** HĐLĐ / phụ lục / thử việc có ghi mức lương của người lao động, theo thứ tự hiệu lực. */
export const salCons = (env: PayEnv, eid: string): SalaryContract[] => env.contracts
  .filter(c => c.empId === eid && ['hdld', 'pl', 'tv'].includes(c.kind) && +(c.salary as number) > 0)
  .sort((a, b) => (a.fromDate || a.signDate || '').localeCompare(b.fromDate || b.signDate || '') || Number(a.kind === 'pl') - Number(b.kind === 'pl'));

export interface SalaryFor { lbl: string; vals: AllowVals; src: string; c?: SalaryContract }

/** Mức lương áp dụng cho tháng m: theo hồ sơ | tự động HĐLĐ/PLHĐ mới nhất có hiệu lực | một HĐ/PL cụ thể. */
export function salaryFor(env: PayEnv, e: Pick<EmployeeLite, 'id' | 'salSrc'>, m?: string): SalaryFor {
  const src = e.salSrc || 'profile', end = m ? m + '-31' : env.today;
  if (src === 'profile') return { lbl: 'Theo hồ sơ', vals: {}, src };
  if (src !== 'auto') {
    const c = env.contracts.find(x => x.id === src && x.empId === e.id);
    return c ? { lbl: `${CK_KINDS[c.kind][1]} ${c.no}`, vals: conSal(c), c, src } : { lbl: 'Theo hồ sơ (HĐ đã chọn không còn)', vals: {}, src };
  }
  const c = salCons(env, e.id).filter(x => (x.fromDate || x.signDate || '') <= end).pop();
  return c ? { lbl: `Tự động: ${CK_KINDS[c.kind][1]} ${c.no}`, vals: conSal(c), c, src } : { lbl: 'Tự động: chưa có HĐ có lương → theo hồ sơ', vals: {}, src };
}

/* ---------- tính lương ---------- */
/** Phụ cấp chịu thuế / không chịu thuế. Hồ sơ cũ chỉ có hai ô tổng allowTax/allowFree. */
export function allowSplit(env: PayEnv, e: Record<string, unknown>): { at: number; af: number } {
  const has = ALLOW.some(([k]) => e['a_' + k] !== undefined && e['a_' + k] !== '');
  if (!has) return { at: +(e.allowTax as number) || 0, af: +(e.allowFree as number) || 0 };
  const p = P(env);
  let at = 0, af = 0;
  for (const [k] of ALLOW) { const v = +(e['a_' + k] as number) || 0; if (+p['tx_' + k]) at += v; else af += v; }
  return { at, af };
}

/** Loại HĐ tính lương: CT chính thức · TV thử việc · DV dịch vụ / cộng tác. */
export const payTypeOf = (e: Pick<EmployeeLite, 'payType' | 'status'>): string => e.payType || ((e.status || 'working') === 'probation' ? 'TV' : 'CT');

export interface PayCalc {
  srcLbl: string; C: string; D: string; E: string;
  K: number; L: number; M: number; N: number; O: number; P: number; Q: number; R: number; S: number; T: number; U: number; V: number; W: number;
  X: number; Y: number; Z: number; AA: number; AB: number; AC: number; AD: number; AE: number; AF: number; AG: number; AH: number;
  AI: number; AJ: number; AK: number; AL: number; AM: number; AN: number; AO: number; AP: number; AQ: number; AR: number; AS: number; AT: number;
  kpi: string; kpiName: string; kpiPct: number | null; hasTs: boolean; insType: string;
  std: number; paid: number; work: number; leave: number; base: number; gross: number; eSI: number; eHI: number; eUI: number; tax: number; tni: number; net: number; cIns: number; adv: number; noIns: boolean;
  [k: string]: unknown;
}

/**
 * Công thức theo file "Bảng lương FAS" (BL_FAS):
 *  W  Thu nhập danh nghĩa = K + L + M + N + O + P + Q + R + S
 *  Z  Tổng lương thực tế = ROUND(W / Y × X)          X = tổng ngày công tính lương, Y = ngày công chuẩn
 *  AB Tổng lương thực tế trước thuế = Z + AA + U + V
 *  AC Lương đóng BH = 0 nếu TV/DV hoặc nghỉ ≥ ngưỡng ngày; ngược lại = K (lương chính)
 *  AD..AG công ty: KPCĐ 2%, BHXH 17.5%, BHYT 3%, BHTN 1%  (đối tượng chỉ đóng TNLĐ-BNN: BHXH 0.5%, không BHYT/BHTN, NLĐ không đóng)
 *  AI..AK NLĐ: 8% / 1.5% / 1%
 *  AM TN chịu thuế = CT: AB − min(M, trần ăn trưa) − O − P − min(Q, trần VPP);  TV/DV: AB
 *  AO TN tính thuế = CT: max(AM − AL − giảm trừ bản thân − giảm trừ NPT × số NPT, 0);  TV/DV: AM
 *  AP Thuế = CT: lũy tiến 5 bậc;  TV/DV: 10% nếu AO ≥ ngưỡng, ngược lại 0
 *  AT Thực lĩnh = AB − AL − AP − AS − AQ + AR
 */
export function calcPay(env: PayEnv, eIn: EmployeeLite, m: string, inpIn?: PayInputs | null): PayCalc {
  const sf = salaryFor(env, eIn, m);
  const e: Record<string, unknown> = { ...eIn, ...sf.vals };
  const p = P(env, m), ts = env.timesheets[m], hasTs = !!ts?.cells?.[eIn.id]?.some(Boolean), s = tsSum(env, ts?.cells?.[eIn.id] || []);
  const inp: PayInputs = inpIn || env.payrolls[m]?.inputs?.[eIn.id] || {};
  const v = (k: string) => +(inp[k] as number) || 0, C = payTypeOf(e as EmployeeLite), D = C === 'CT' ? 'Yes' : 'No', E = C === 'CT' ? 'LT' : 'TP';
  const Y = +(ts?.std as number) || stdDays(m, !!ts?.sat), X = inp.days !== undefined && inp.days !== '' && inp.days !== null ? +inp.days : ts ? s.AY : Y;
  const ks = empKpi(env, e as EmployeeLite), rk = ks ? (ks.ranks.find(x => x.r === inp.kpi) || ks.ranks[0]) : null, kr = rk ? (+rk.pct || 0) / 100 : 1, rank = rk ? rk.r : '';
  const n = (k: string) => +(e[k] as number) || 0;
  const K = n('baseSalary'), L = Math.round(n('a_perf') * kr), M = n('a_lunch'), N = n('a_fuel'), O = n('a_phone'), Pp = n('a_travel'), Q = n('a_stationery');
  const R = v('kqkd'), S = inp.resp !== undefined && inp.resp !== '' && inp.resp !== null ? +inp.resp : Math.round(n('a_resp') * (ks?.base === 'perf_resp' ? kr : 1)), T = v('lg'), U = v('peak'), V = v('biz'), AA = v('adj');
  const W = K + L + M + N + O + Pp + Q + R + S, Z = Y ? Math.round(W / Y * X) : 0, AB = Z + AA + U + V;
  const it = (e.insType as string) || 'full', noIns = C !== 'CT' || it === 'none' || (Y - X) >= (+p.noInsDays || 14);
  const AC = noIns ? 0 : Math.min(K, +p.capSI || Infinity), bUI = noIns ? 0 : Math.min(K, +p.capUI || Infinity);
  const tn = it === 'tnld', AD = AC * p.cTU / 100, AE = AC * (tn ? p.cTNLD : p.cSI) / 100, AF = tn ? 0 : AC * p.cHI / 100, AG = tn ? 0 : bUI * p.cUI / 100, AH = AD + AE + AF + AG;
  const AI = tn ? 0 : AC * p.eSI / 100, AJ = tn ? 0 : AC * p.eHI / 100, AK = tn ? 0 : bUI * p.eUI / 100, AL = AI + AJ + AK;
  const AM = AB === 0 ? 0 : C === 'CT' ? AB - Math.min(M, +p.lunchCap || 0) - O - Pp - Math.min(Q, +p.vppCap || 0) : AB;
  const AN = n('dependents'), AO = C === 'CT' ? Math.max(AM - AL - p.pers - p.dep * AN, 0) : AM;
  const AP = Math.round(E === 'TP' ? (AO >= (+p.flatMin || 0) ? AO * (+p.flatTax || 0) / 100 : 0) : pit(AO));
  const AQ = v('taxDed'), AR = v('taxRef'), AS = v('adv'), AT = AB - AL - AP - AS - AQ + AR;
  return {
    srcLbl: sf.lbl, C, D, E, K, L, M, N, O, P: Pp, Q, R, S, T, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG, AH, AI, AJ, AK, AL, AM, AN, AO, AP, AQ, AR, AS, AT,
    kpi: rank, kpiName: ks ? ks.name : '', kpiPct: rk ? +rk.pct : null, hasTs, insType: it,
    // tên cũ để các màn hình khác dùng
    std: Y, paid: X, work: s.n.AN, leave: s.n.AO + s.n.AP, base: K, gross: AB, eSI: AI, eHI: AJ, eUI: AK, tax: AP, tni: AO, net: AT, cIns: AH, adv: AS, noIns,
  };
}

/* ---------- bảng lương: cột, nhãn, dòng ---------- */
/** Nhãn cột có tỷ lệ theo bộ thông số của tháng. */
export function payLbl(l: string, k: string, p: PayParams): string {
  const r = (x: unknown) => String(+(x as number) || 0).replace('.', ',');
  return ({
    AD: `KPCĐ (${r(p.cTU)}%)`, AE: `BHXH (${r(p.cSI)}%)`, AF: `BHYT (${r(p.cHI)}%)`, AG: `BHTN (${r(p.cUI)}%)`, AH: `Tổng BHXH Cty ${r(+p.cTU + +p.cSI + +p.cHI + +p.cUI)}%`,
    AI: `BHXH (${r(p.eSI)}%)`, AJ: `BHYT (${r(p.eHI)}%)`, AK: `BHTN (${r(p.eUI)}%)`, AL: `Tổng BHXH NLĐ ${r(+p.eSI + +p.eHI + +p.eUI)}%`,
  } as Record<string, string>)[k] || l;
}

/** [nhãn, khóa, kiểu] – kiểu: t chữ · n số · i:khóa ô nhập số · k KPI */
export const PAY_COLS: [string, string, string][] = [
  ['Kỳ lương', 'per', 't'], ['Kỳ trả', 'payPer', 't'], ['Loại HD', 'C', 't'], ['Bảo hiểm', 'D', 't'], ['Thuế TNCN', 'E', 't'], ['STT', 'stt', 't'], ['Mã NV', 'code', 't'], ['Họ và tên', 'fullName', 't'], ['Chức vụ', 'position', 't'], ['Phòng ban', 'dept', 't'],
  ['Lương chính', 'K', 'n'], ['Lương thực hiện công việc', 'L', 'k'], ['HT Ăn trưa', 'M', 'n'], ['HT Xăng xe', 'N', 'n'], ['HT Điện thoại', 'O', 'n'], ['HT Công tác phí', 'P', 'n'], ['HT văn phòng phẩm', 'Q', 'n'], ['Thưởng KQKD', 'R', 'i:kqkd'], ['Hỗ trợ trách nhiệm', 'S', 'i:resp'],
  ['Lãi gộp thực hiện', 'T', 'i:lg'], ['Hỗ trợ cao điểm', 'U', 'i:peak'], ['Lương kinh doanh', 'V', 'i:biz'], ['Thu Nhập Danh Nghĩa', 'W', 'n'], ['Ngày công thực tế', 'X', 'i:days'], ['Ngày công tháng', 'Y', 'n'], ['Tổng lương thực tế', 'Z', 'n'], ['Điều chỉnh lương trước thuế', 'AA', 'i:adj'], ['Tổng lương thực tế trước thuế', 'AB', 'n'],
  ['Lương đóng bảo hiểm', 'AC', 'n'], ['KPCĐ (2%)', 'AD', 'n'], ['BHXH (17.5%)', 'AE', 'n'], ['BHYT (3%)', 'AF', 'n'], ['BHTN (1%)', 'AG', 'n'], ['Tổng BHXH Cty 23.5%', 'AH', 'n'], ['BHXH (8%)', 'AI', 'n'], ['BHYT (1,5%)', 'AJ', 'n'], ['BHTN (1%)', 'AK', 'n'], ['Tổng BHXH NLĐ 10.5%', 'AL', 'n'],
  ['Thu nhập chịu thuế', 'AM', 'n'], ['Người phụ thuộc', 'AN', 'n'], ['Thu nhập tính thuế', 'AO', 'n'], ['Thuế TNCN', 'AP', 'n'], ['Trừ thuế TNCN quyết toán', 'AQ', 'i:taxDed'], ['Hoàn thuế TNCN quyết toán', 'AR', 'i:taxRef'], ['Tạm ứng', 'AS', 'i:adv'], ['Thực lĩnh', 'AT', 'n'],
  ['Số tài khoản', 'bankNo', 't'], ['Tên Tài khoản', 'bankHolder', 't'], ['Ngân hàng', 'bankName', 't'],
];
/** Các cột có dòng tổng cộng. */
export const PAY_SUM = new Set(['K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'Z', 'AA', 'AB', 'AC', 'AD', 'AE', 'AF', 'AG', 'AH', 'AI', 'AJ', 'AK', 'AL', 'AM', 'AO', 'AP', 'AQ', 'AR', 'AS', 'AT']);

export const nextPer = (m: string): string => { const [y, mo] = m.split('-').map(Number); return mo === 12 ? `${y + 1}01` : `${y}${String(mo + 1).padStart(2, '0')}`; };

/** Người lao động có mặt trong tháng, theo mã. */
export const payList = (env: PayEnv, m: string): EmployeeLite[] => env.employees.filter(e => empInMonth(e, m)).sort((a, b) => (a.code || '').localeCompare(b.code || ''));

export interface PayRow { e: Record<string, unknown> & { id: string }; p: PayCalc | (Record<string, unknown> & { id: string }) }

/** Các dòng bảng lương của tháng: bảng đã chốt lấy từ ảnh chụp, chưa chốt thì tính từ hồ sơ + ô nhập. */
export function payRows(env: PayEnv, m: string, inputs: Record<string, PayInputs>): PayRow[] {
  const saved = env.payrolls[m];
  if (saved?.locked) return (saved.snapshot || []).map(x => ({ e: { id: x.id, code: x.code, fullName: x.fullName, position: x.position, dept: x.dept, bankNo: x.bankNo, bankHolder: x.bankHolder, bankName: x.bankName }, p: x }));
  return payList(env, m).map(e => ({ e: e as PayRow['e'], p: calcPay(env, e, m, inputs[e.id]) })).filter(({ p }) => (p as PayCalc).AB || (p as PayCalc).AT);
}

export const payCell = (e: Record<string, unknown>, p: Record<string, unknown>, key: string, m: string, i: number): unknown =>
  key === 'per' ? m.replace('-', '') : key === 'payPer' ? nextPer(m) : key === 'stt' ? i + 1 : key in p ? p[key] : (e[key] ?? '');

/* ---------- trích nộp BHXH & KPCĐ ---------- */
/** Tách các khoản bảo hiểm cho bảng trích nộp: BHXH công ty 17,5% = hưu trí tử tuất 14% + ốm đau thai sản 3% + TNLĐ-BNN 0,5%. */
export function insSplit(p: Record<string, unknown>) {
  const AC = +(p.AC as number) || 0, tn = p.insType === 'tnld', f = (k: string) => +(p[k] as number) || 0;
  return {
    AC, eHT: f('AI'), eOD: 0, eTN: 0, eYT: f('AJ'), eTNghiep: f('AK'), eSum: f('AL'),
    cHT: tn ? 0 : AC * 14 / 100, cOD: tn ? 0 : AC * 3 / 100, cTN: tn ? f('AE') : AC * 0.5 / 100, cYT: f('AF'), cTNghiep: f('AG'), cSum: f('AE') + f('AF') + f('AG'), kpcd: f('AD'),
  };
}
